import React, { useRef, useState, useEffect } from 'react';
import {
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  StickyNote,
  Trash2,
  ZoomIn,
  Download,
  Repeat
} from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import engagementTracker from '../services/engagement';
import TelestrationCanvas from './TelestrationCanvas';

const VideoPlayer = ({ videoUrl, videoId, teamId, onTimeUpdate, onClipMark, onAddNote, onEnded, annotations = [], onAnnotationCreate, onDeleteAnnotation, onPreviousVideo, onNextVideo }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [showControls, setShowControls] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLooping, setIsLooping] = useState(false);

  const [isKKeyHeld, setIsKKeyHeld] = useState(false);
  const [isPrevHeld, setIsPrevHeld] = useState(false);
  const [isNextHeld, setIsNextHeld] = useState(false);
  const kKeyTimeoutRef = useRef(null);
  const previousSpeedRef = useRef(1);
  const prevHoldTimeoutRef = useRef(null);
  const nextHoldTimeoutRef = useRef(null);
  const isPrevHoldingRef = useRef(false);
  const isNextHoldingRef = useRef(false);
  const reversePlaybackIntervalRef = useRef(null);

  // Annotation states
  const [isDrawing, setIsDrawing] = useState(false);
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [viewedAnnotations, setViewedAnnotations] = useState(new Set()); // Track viewed in this session

  // Zoom state
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [transformOrigin, setTransformOrigin] = useState('center');

  // Handle video source changes without unmounting (preserves fullscreen)
  const prevVideoUrlRef = useRef(videoUrl);
  useEffect(() => {
    if (videoUrl && videoUrl !== prevVideoUrlRef.current) {
      prevVideoUrlRef.current = videoUrl;
      // Reset playback state for new video
      setCurrentTime(0);
      setDuration(0);
      setViewedAnnotations(new Set());
      setActiveAnnotation(null);
      setIsDrawing(false);
      setZoomLevel(1);
      setTransformOrigin('center');
      setIsZoomMode(false);

      // Load and play new video
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
        // The src will update via authenticatedUrl memo, 
        // then we auto-play after it loads
        const handleCanPlay = () => {
          const playPromise = videoRef.current?.play();
          if (playPromise !== undefined) {
            playPromise.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
          }
          videoRef.current?.removeEventListener('canplay', handleCanPlay);
        };
        // Small delay to let React update the src attribute
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.load();
            videoRef.current.addEventListener('canplay', handleCanPlay, { once: true });
          }
        }, 50);
      }
    }
  }, [videoUrl]);

  // Safety sync: ensure isPlaying state matches actual video state
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (videoRef.current) {
        const actuallyPaused = videoRef.current.paused;
        if (isPlaying && actuallyPaused && !isKKeyHeld) {
          // Video is paused but state says playing - fix it
          setIsPlaying(false);
        }
      }
    }, 500);
    return () => clearInterval(syncInterval);
  }, [isPlaying, isKKeyHeld]);

  // K key hold behavior - play at 0.5x when held
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key.toLowerCase() === 'k' && !isKKeyHeld && !e.repeat) {
        setIsKKeyHeld(true);

        // Save current speed and playback state
        if (videoRef.current) {
          previousSpeedRef.current = videoRef.current.playbackRate;
          videoRef.current.playbackRate = 0.5;
          setPlaybackSpeed(0.5);

          // If video is paused, start playing
          if (videoRef.current.paused) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
              playPromise.then(() => setIsPlaying(true)).catch(e => console.error("Play failed:", e));
            } else {
              setIsPlaying(true);
            }
          }
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key.toLowerCase() === 'k' && isKKeyHeld) {
        setIsKKeyHeld(false);

        // Pause video and restore previous speed
        if (videoRef.current) {
          videoRef.current.pause();
          setIsPlaying(false);
          videoRef.current.playbackRate = previousSpeedRef.current;
          setPlaybackSpeed(previousSpeedRef.current);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isKKeyHeld]);

  // Navigation button hold handlers
  const handlePrevMouseDown = (e) => {
    e.preventDefault(); // Prevent any default behavior
    isPrevHoldingRef.current = false;
    prevHoldTimeoutRef.current = setTimeout(() => {
      // Hold detected - start REVERSE playback at 0.5x speed
      isPrevHoldingRef.current = true;
      setIsPrevHeld(true);
      if (videoRef.current) {
        previousSpeedRef.current = videoRef.current.playbackRate;
        // IMPORTANT: Pause the video first, then manually seek backwards
        if (!videoRef.current.paused) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
        // Start reverse playback (seek backwards frame by frame)
        reversePlaybackIntervalRef.current = setInterval(() => {
          if (videoRef.current && videoRef.current.currentTime > 0) {
            // Move backwards at 0.5x speed: go back 1 frame every 2 intervals (~15fps backwards)
            videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - (1 / 30));
          }
        }, 66); // ~15fps for 0.5x reverse speed
      }
    }, 200);
  };

  const handlePrevMouseUp = (e) => {
    e.preventDefault();
    clearTimeout(prevHoldTimeoutRef.current);
    clearInterval(reversePlaybackIntervalRef.current);
    console.log("Prev mouseUp - isHolding:", isPrevHoldingRef.current);

    if (isPrevHoldingRef.current) {
      // Was a hold - just stop reverse playback, don't navigate
      console.log("Prev was HOLD - stopping reverse");
      setIsPrevHeld(false);
      isPrevHoldingRef.current = false;
    } else {
      // Was a quick click - navigate to previous video
      console.log("Prev was CLICK - navigating");
      onPreviousVideo && onPreviousVideo();
    }
  };

  const handlePrevMouseLeave = () => {
    // Just clean up, don't navigate
    clearTimeout(prevHoldTimeoutRef.current);
    clearInterval(reversePlaybackIntervalRef.current);
    if (isPrevHoldingRef.current) {
      setIsPrevHeld(false);
      isPrevHoldingRef.current = false;
    }
  };

  const handleNextMouseDown = (e) => {
    e.preventDefault();
    console.log("Next mouseDown");
    isNextHoldingRef.current = false; // FIX: was isPrevHoldingRef
    nextHoldTimeoutRef.current = setTimeout(() => {
      // Hold detected - start forward playback at 0.5x speed
      console.log("Next HOLD detected");
      isNextHoldingRef.current = true;
      setIsNextHeld(true);
      if (videoRef.current) {
        previousSpeedRef.current = videoRef.current.playbackRate;
        videoRef.current.playbackRate = 0.5;
        setPlaybackSpeed(0.5);
        if (videoRef.current.paused) {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.then(() => setIsPlaying(true)).catch(e => console.error("Play failed:", e));
          } else {
            setIsPlaying(true);
          }
        }
      }
    }, 200);
  };

  const handleNextMouseUp = (e) => {
    e.preventDefault();
    clearTimeout(nextHoldTimeoutRef.current);
    console.log("Next mouseUp - isHolding:", isNextHoldingRef.current);

    if (isNextHoldingRef.current) {
      // Was a hold - restore playback, don't navigate
      console.log("Next was HOLD - restoring playback");
      setIsNextHeld(false);
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.playbackRate = previousSpeedRef.current;
        setPlaybackSpeed(previousSpeedRef.current);
      }
      isNextHoldingRef.current = false;
    } else {
      // Was a quick click - navigate to next video
      console.log("Next was CLICK - navigating");
      onNextVideo && onNextVideo();
    }
  };

  const handleNextMouseLeave = () => {
    // Just clean up, don't navigate
    clearTimeout(nextHoldTimeoutRef.current);
    if (isNextHoldingRef.current) {
      setIsNextHeld(false);
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.playbackRate = previousSpeedRef.current;
        setPlaybackSpeed(previousSpeedRef.current);
      }
      isNextHoldingRef.current = false;
    }
  };

  // Check for annotations during playback
  useEffect(() => {
    if (!isPlaying || isDrawing || activeAnnotation) return;

    const checkAnnotations = () => {
      const match = annotations.find(a =>
        Math.abs(a.timestamp - currentTime) < 0.2 // 200ms window
      );

      if (match && !viewedAnnotations.has(match.id)) {
        // Pause and show annotation
        if (videoRef.current) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
        setActiveAnnotation(match);
        setViewedAnnotations(prev => new Set(prev).add(match.id));
      }
    };
    checkAnnotations();
  }, [currentTime, annotations, isPlaying, viewedAnnotations, isDrawing, activeAnnotation]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key.toLowerCase()) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'j': // Step back 1 frame (assuming 30fps)
          e.preventDefault();
          stepFrame(-1 / 30);
          break;
        case 'l': // Increase speed
          e.preventDefault();
          cycleSpeed();
          break;
        case 'n': // New note
          e.preventDefault();
          handleStartDrawing();
          break;
        case 'p': // Previous video
          e.preventDefault();
          onPreviousVideo && onPreviousVideo();
          break;
        case 'arrowleft':
          e.preventDefault();
          if (e.shiftKey) {
            onPreviousVideo && onPreviousVideo();
          } else {
            seek(currentTime - 5);
          }
          break;
        case 'arrowright':
          e.preventDefault();
          if (e.shiftKey) {
            onNextVideo && onNextVideo();
          } else {
            seek(currentTime + 5);
          }
          break;
        case 'f': // Toggle fullscreen
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'r': // Toggle loop
          e.preventDefault();
          setIsLooping(prev => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentTime, playbackSpeed]);

  const togglePlay = () => {
    // If viewing annotation, clear it and resume
    if (activeAnnotation) {
      setActiveAnnotation(null);
    }

    if (videoRef.current) {
      // Use the actual video element state, not React state
      if (!videoRef.current.paused) {
        videoRef.current.pause();
        // Also clean up any active reverse playback
        clearInterval(reversePlaybackIntervalRef.current);
        engagementTracker.stopTracking();
        engagementTracker.stopHeartbeat();
        setIsPlaying(false);
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            if (videoId && teamId) {
              engagementTracker.startTracking(videoId, teamId);
              engagementTracker.startHeartbeat();
            }
            setIsPlaying(true);
          }).catch(error => {
            console.error("Playback failed", error);
            setIsPlaying(false);
          });
        } else {
          if (videoId && teamId) {
            engagementTracker.startTracking(videoId, teamId);
            engagementTracker.startHeartbeat();
          }
          setIsPlaying(true);
        }
      }
    }
  };

  const stepFrame = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const seek = (time) => {
    if (videoRef.current) {
      const newTime = Math.max(0, Math.min(time, duration));
      videoRef.current.currentTime = newTime;
      // Clear viewed annotations ahead of seek?
      setViewedAnnotations(prev => {
        const newSet = new Set(prev);
        annotations.forEach(a => {
          if (a.timestamp > newTime) newSet.delete(a.id);
        });
        return newSet;
      });
      engagementTracker.onSeek(newTime);
    }
  };

  const cycleSpeed = () => {
    const speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
    if (videoRef.current) {
      videoRef.current.playbackRate = nextSpeed;
    }
  };

  const handleStartDrawing = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    setIsDrawing(true);
    engagementTracker.onNote(currentTime);
  };

  const handleSaveAnnotation = (data) => {
    onAnnotationCreate && onAnnotationCreate({
      timestamp: currentTime,
      data: data
    });
    setIsDrawing(false);
  };

  const handleCancelDrawing = () => {
    setIsDrawing(false);
  };

  // Cleanup and Fullscreen listeners
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(isFull);
      if (!isFull) {
        if (window.screen?.orientation?.unlock) {
          window.screen.orientation.unlock();
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      engagementTracker.cleanup();
    };
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Fullscreen controls auto-hide
  const resetControlsTimeout = () => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    if (isFullscreen && isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  useEffect(() => {
    if (isFullscreen && isPlaying) {
      resetControlsTimeout();
    } else {
      setShowControls(true);
      clearTimeout(controlsTimeoutRef.current);
    }
    return () => clearTimeout(controlsTimeoutRef.current);
  }, [isFullscreen, isPlaying]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        const container = containerRef.current;
        if (container?.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container?.webkitRequestFullscreen) {
          await container.webkitRequestFullscreen();
        } else if (videoRef.current?.webkitEnterFullscreen) {
          videoRef.current.webkitEnterFullscreen();
        }

        setIsFullscreen(true);

        // Attempt to lock to landscape on supported devices (Android / desktop PWA)
        if (window.screen?.orientation?.lock) {
          try {
            await window.screen.orientation.lock('landscape');
          } catch (e) {
            console.log('Orientation lock not supported or permitted', e);
          }
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }

        setIsFullscreen(false);

        // Unlock orientation
        if (window.screen?.orientation?.unlock) {
          window.screen.orientation.unlock();
        }
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
    }
  };

  const handleDownload = async () => {
    if (!authenticatedUrl) return;
    try {
      const response = await fetch(authenticatedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Extract filename or default
      a.download = `clip-${videoId}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error("Download failed:", e);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  // Append auth token to URL for secure streaming
  const authenticatedUrl = React.useMemo(() => {
    if (!videoUrl) return undefined;
    if (videoUrl.startsWith('blob:')) return videoUrl;
    if (videoUrl.includes('token=')) return videoUrl;

    const token = localStorage.getItem('token');
    if (token) {
      const separator = videoUrl.includes('?') ? '&' : '?';
      return `${videoUrl}${separator}token=${token}`;
    }
    return videoUrl;
  }, [videoUrl]);

  return (
    <div
      ref={containerRef}
      className={`bg-black rounded-lg overflow-hidden ${isFullscreen ? 'relative w-screen h-screen flex flex-col' : ''}`}
      onMouseMove={isFullscreen ? resetControlsTimeout : undefined}
      onTouchStart={isFullscreen ? resetControlsTimeout : undefined}
    >
      {/* Video Element */}
      <div className={`relative bg-black overflow-hidden ${isFullscreen ? 'flex-1 min-h-0' : 'aspect-video'}`}>
        <video
          ref={videoRef}
          src={authenticatedUrl}
          className={`w-full h-full ${isFullscreen ? 'object-contain' : ''}`}
          onTimeUpdate={(e) => {
            setCurrentTime(e.target.currentTime);
            onTimeUpdate && onTimeUpdate(e.target.currentTime);
          }}
          onLoadedMetadata={(e) => setDuration(e.target.duration)}
          onEnded={() => {
            if (isLooping && videoRef.current) {
              videoRef.current.currentTime = 0;
              videoRef.current.play().catch(() => {});
            } else {
              onEnded && onEnded();
            }
          }}
          onClick={(e) => {
            if (isZoomMode) {
              e.stopPropagation();

              // Calculate click position percentage to zoom towards cursor
              const rect = e.target.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;

              setTransformOrigin(`${x}% ${y}%`);
              setZoomLevel(prev => Math.min(prev + 1, 3));
            } else {
              togglePlay();
            }
          }}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFullscreen();
          }}
          onContextMenu={(e) => {
            if (isZoomMode) {
              e.preventDefault();
              e.stopPropagation();
              setZoomLevel(prev => {
                const newZoom = Math.max(prev - 1, 1);
                if (newZoom === 1) setTransformOrigin('center');
                return newZoom;
              });
            }
          }}
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: transformOrigin,
            cursor: isZoomMode ? (zoomLevel < 3 ? 'zoom-in' : 'zoom-out') : 'pointer',
            transition: 'transform 0.2s ease-out'
          }}
        />



        {/* Telestration Overlay */}
        {(isDrawing || activeAnnotation) && (
          <TelestrationCanvas
            readOnly={!isDrawing}
            initialData={activeAnnotation ? activeAnnotation.data : null}
            authorName={activeAnnotation ? activeAnnotation.user_name : null}
            onSave={handleSaveAnnotation}
            onCancel={handleCancelDrawing}
          />
        )}
      </div>

      {/* Controls */}
      <div
        className={`bg-[#1a1a1b] p-4 transition-all duration-300 shrink-0 ${isFullscreen ? `bg-[#0a0a0b]/95 backdrop-blur-sm border-t border-gray-800/50 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}` : ''}`}
        onMouseEnter={isFullscreen ? () => setShowControls(true) : undefined}
      >
        {/* Timeline */}
        <div className="mb-4 relative group/timeline">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={0.01}
            onValueChange={(value) => seek(value[0])}
            className="cursor-pointer relative z-10"
          />

          {/* Annotation Markers */}
          {duration > 0 && annotations.map((annotation) => {
            const position = (annotation.timestamp / duration) * 100;
            return (
              <div
                key={annotation.id}
                className="absolute top-1/2 -translate-y-1/2 -mt-[8px] w-4 h-4 bg-yellow-500 rounded-full z-20 cursor-pointer group/marker hover:scale-125 transition-transform shadow-sm border border-black/20"
                style={{ left: `calc(${position}% - 6px)` }}
              >
                {/* Tooltip Container with Hover Bridge (pb-3 fills the gap) */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 pb-3 hidden group-hover/marker:flex flex-col items-center z-30">
                  <div className="bg-[#1a1a1b] p-3 rounded-md text-xs text-white whitespace-nowrap border border-gray-700 shadow-xl min-w-[120px] relative">
                    <div className="mb-1 font-semibold text-yellow-500 uppercase tracking-wider text-[10px] text-center">Note</div>
                    <div className="max-w-[200px] truncate mb-3 text-gray-200 text-center">{annotation.data?.text || "Visual Note"}</div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("Delete clicked:", annotation.id, "Handler:", onDeleteAnnotation);
                        onDeleteAnnotation && onDeleteAnnotation(annotation.id);
                      }}
                      className="w-full bg-red-600/10 hover:bg-red-600/20 text-red-500 hover:text-red-400 border border-red-600/20 px-3 py-1.5 rounded flex items-center justify-center gap-2 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1a1a1b]"></div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={togglePlay}
              className="text-white hover:bg-gray-700"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>


            <Button
              variant="ghost"
              size="icon"
              onMouseDown={handlePrevMouseDown}
              onMouseUp={handlePrevMouseUp}
              onMouseLeave={handlePrevMouseLeave}
              className="text-white hover:bg-gray-700"
              title="Previous Video (Hold for reverse 0.5x)"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onMouseDown={handleNextMouseDown}
              onMouseUp={handleNextMouseUp}
              onMouseLeave={handleNextMouseLeave}
              className="text-white hover:bg-gray-700"
              title="Next Video (Hold for 0.5x speed)"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>

            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className="text-white hover:bg-gray-700"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              <Slider
                value={[isMuted ? 0 : volume * 100]}
                max={100}
                step={1}
                onValueChange={(value) => {
                  const newVolume = value[0] / 100;
                  setVolume(newVolume);
                  if (videoRef.current) {
                    videoRef.current.volume = newVolume;
                  }
                  if (newVolume > 0) setIsMuted(false);
                }}
                className="w-24"
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={cycleSpeed}
              className="text-white hover:bg-gray-700 ml-4 min-w-[60px]"
              title="Speed (L)"
            >
              {playbackSpeed}x
            </Button>
          </div>

          <div className="flex items-center gap-2">

            {/* DOWNLOAD BUTTON */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownload}
              className="text-white hover:bg-gray-700 hover:text-green-400"
              title="Download Clip"
            >
              <Download className="w-5 h-5" />
            </Button>

            <Button
              variant={isDrawing ? "default" : "ghost"}
              size="sm"
              onClick={handleStartDrawing}
              disabled={isDrawing || !!activeAnnotation}
              className={isDrawing ? "bg-yellow-600 text-white" : "text-white hover:bg-gray-700"}
              title="Add Note (N)"
            >
              <StickyNote className="w-4 h-4 mr-1" />
              Note
            </Button>

            <Button
              variant={isZoomMode ? "default" : "ghost"}
              size="icon"
              onClick={() => {
                setIsZoomMode(!isZoomMode);
                if (isZoomMode) setZoomLevel(1); // Reset zoom when turning off
              }}
              className={isZoomMode ? "bg-blue-600 text-white" : "text-white hover:bg-gray-700"}
              title="Toggle Zoom Mode (Left click: In, Right click: Out)"
            >
              <ZoomIn className="w-5 h-5" />
            </Button>

            <Button
              variant={isLooping ? "default" : "ghost"}
              size="icon"
              onClick={() => setIsLooping(!isLooping)}
              className={isLooping ? "bg-purple-600 text-white" : "text-white hover:bg-gray-700"}
              title="Loop Video (R)"
            >
              <Repeat className="w-5 h-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-white hover:bg-gray-700"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Keyboard shortcuts hint */}
        {!isFullscreen && (
          <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-4">
            <span>Space: Play/Pause</span>
            <span>J/K: Frame ±1</span>
            <span>L: Speed</span>
            <span>N: New Note</span>
            <span>←/→: ±5s</span>
            <span>P: Prev Video</span>
            <span>⇧←/⇧→: Prev/Next</span>
            <span>F: Fullscreen</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
